import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
} from '@angular/core';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { TuiInputModule } from '@taiga-ui/kit';
import {
  BehaviorSubject,
  catchError,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { AlertService } from '@shared/services/alert.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BuyerService } from '@shared/services/buyer.service';
import { ClientService } from '@shared/services/client.service';
import { TuiLetModule } from '@taiga-ui/cdk';
import { AsyncPipe } from '@angular/common';
import { FarmService } from '@shared/services/farm.service';
import { asCatalogDetails, prepareCatalog } from '@shared/utils/catalogs';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import { Buyer, BuyerForm } from '../../../../types/buyer';

export type BuyerFormInputData = {
  buyer: Buyer;
};

@Component({
  selector: 'app-buyer-form',
  standalone: true,
  imports: [
    AutocompleteComponent,
    TuiButtonModule,
    TuiInputModule,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    TuiLetModule,
    AsyncPipe,
    CheckboxComponent,
  ],
  templateUrl: './buyer-form.component.html',
  styleUrl: './buyer-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyerFormComponent implements OnInit {
  save$ = new Subject<void>();
  isEdit$ = new BehaviorSubject(false);

  title$ = this.isEdit$.pipe(
    map(value => (value ? 'Редактировать' : 'Создать').concat(' покупателя')),
  );

  buyerForm = this.fb.group<BuyerForm>({
    id: this.fb.control<Nullable<number>>(null),
    uniqueKey: this.fb.control(''),
    name: this.fb.control<Nullable<string>>(null),
    clientId: this.fb.control<Nullable<number>>(null),
    code: this.fb.control<Nullable<string>>(null, Validators.required),
    farmId: this.fb.control<Nullable<number>>(null),
    publicAccess: this.fb.control(null),
  });

  farms$ = this.farmService.getAll().pipe(
    map(value => prepareCatalog(asCatalogDetails(value), true)),
    shareReplay(),
  );

  clients$ = this.clientService.getAll().pipe(
    map(value => prepareCatalog(asCatalogDetails(value), true)),
    shareReplay(),
  );

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    protected readonly context: TuiDialogContext<void, BuyerFormInputData>,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly alert: AlertService,
    private readonly buyerService: BuyerService,
    private readonly clientService: ClientService,
    private readonly farmService: FarmService,
  ) {}

  onSaveClick() {
    this.save$
      .pipe(
        switchMap(() =>
          this.buyerService.update(this.buyerForm.getRawValue() as Buyer),
        ),
        tap(() => this.closeOnSave()),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  closeOnSave() {
    this.context.$implicit.next();
    this.context.$implicit.complete();
  }

  setForm() {
    if (this.context.data.buyer) {
      this.buyerForm.setValue(this.context.data.buyer);
    }
  }

  // fetchClientInfo() {
  //   const clientId = this.context.data.buyer.clientId;
  //   if (!clientId) {
  //     return;
  //   }
  //   this.clientService
  //     .getById(clientId)
  //     .pipe(take(1))
  //     .subscribe(client => {
  //       this.client$.next(client);
  //     });
  // }

  // fetchFarmInfo() {
  //   const farmId = this.context.data.buyer.farmId;
  //   if (!farmId) {
  //     return;
  //   }
  //   this.farmService
  //     .getById(farmId)
  //     .pipe(take(1))
  //     .subscribe(farm => {
  //       this.farm$.next(farm);
  //     });
  // }

  ngOnInit() {
    this.isEdit$.next(!!this.context.data.buyer.id);
    this.setForm();
    this.onSaveClick();
    // this.fetchClientInfo();
    // this.fetchFarmInfo();
  }
}
